import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, X, Sparkle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const t = localStorage.getItem('capitalops_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Ordered tour steps — each targets a selector on the live Layout.
// `route` is optional: if set, we navigate there before measuring.
function buildSteps({ isAdmin }) {
  const base = [
    {
      key: 'welcome',
      target: '[data-testid="topbar-brand"]',
      title: 'Welcome to Luminex',
      body: "In under a minute, I'll show you the four corners of the platform. Feel free to skip — you can re-launch this tour any time from the Help icon.",
      placement: 'bottom-left',
    },
    {
      key: 'library',
      target: '[data-testid="nav-operating-models"]',
      route: '/library',
      title: 'Operating Models',
      body: '30 industry verticals, ~7,400 business processes mapped L0 → L4 with swimlanes, RACI, and KPIs. This is your reference library.',
      placement: 'bottom',
    },
    {
      key: 'folders',
      target: '[data-testid="nav-folder-structures"]',
      route: '/folders',
      title: 'Process Creation',
      body: 'Replicate L1 → L2 → L3 from any industry into your own hierarchy, then add BPs — from templates or from a prompt via Generative AI.',
      placement: 'bottom',
    },
    {
      key: 'reeng',
      target: '[data-testid="nav-re-engineering"]',
      route: '/reengineer',
      title: 'Process Re-engineering — the hero feature',
      body: 'Upload a process document (PDF, DOCX, PPTX, even a scanned image). Luminex extracts the as-is swimlane, lets you optimize it, and generates a steering-committee-ready Delta Report with cycle-time & FTE savings.',
      placement: 'bottom',
    },
  ];
  if (isAdmin) {
    base.push({
      key: 'iam',
      target: '[data-testid="nav-team"]',
      route: '/iam',
      title: 'Team — workspace admin',
      body: 'Invite teammates and assign roles (Admin / Edit / View). This tab is only visible to admins.',
      placement: 'bottom',
    });
    base.push({
      key: 'billing',
      target: '[data-testid="nav-billing"]',
      route: '/billing',
      title: 'Billing & credits',
      body: 'Buy credit packs (1 credit = 1 process + 8 AI calls), see invoices with GST, and track view-quota usage. Indian customers get 18% GST line; international invoices are tax-exempt.',
      placement: 'bottom',
    });
    base.push({
      key: 'settings',
      target: '[data-testid="user-avatar"]',
      title: 'Settings & whitelabel branding',
      body: 'Upload your company logo, toggle PDF whitelabel defaults, and manage notification preferences. Open it from the profile menu in the top-right corner. Whitelabel makes every Process Deep Dive and Delta Report PDF carry your branding.',
      placement: 'bottom-left',
    });
  }
  base.push({
    key: 'help',
    target: '[data-testid="help-btn"]',
    title: "You're set — and here's the manual",
    body: 'Re-launch this tour any time from here. The same menu also opens the full User Guide (with downloadable Quick Start + Power-User Manual PDFs) and Vidhā, your in-app AI guide.',
    placement: 'bottom-left',
  });
  return base;
}

function getRect(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom };
}

export default function WalkthroughTour({ user, onClose, onCompleted }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [steps] = useState(() => buildSteps({ isAdmin }));
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  const step = steps[idx];

  // Navigate (if needed) and compute the spotlight rect
  useLayoutEffect(() => {
    let disposed = false;
    (async () => {
      if (step.route && window.location.pathname !== step.route) {
        navigate(step.route);
        await new Promise(r => setTimeout(r, 350)); // let layout settle
      }
      // Measure with retries in case the element mounts late
      for (let i = 0; i < 20; i++) {
        const r = getRect(step.target);
        if (r && r.width > 0) {
          if (!disposed) setRect(r);
          return;
        }
        await new Promise(r => setTimeout(r, 100));
      }
      if (!disposed) setRect(null);
    })();
    return () => { disposed = true; };
  }, [idx, step, navigate]);

  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
      const r = getRect(step.target); if (r) setRect(r);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    // Observe just the target element instead of polling on a timer — avoids main-thread wakeups.
    let ro;
    try {
      const el = document.querySelector(step.target);
      if (el && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(onResize);
        ro.observe(el);
      }
    } catch { /* safari-legacy fallback: do nothing */ }
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      if (ro) ro.disconnect();
    };
  }, [step]);

  const finish = async (markComplete) => {
    if (markComplete) {
      try {
        await axios.post(`${API_URL}/api/users/me/walkthrough-completed`, {}, { headers: authHeader() });
        onCompleted?.();
      } catch { /* non-blocking */ }
    }
    onClose?.();
  };

  const isLast = idx === steps.length - 1;

  // Tooltip position — prefers below the target, flips above if no room
  const pad = 12;
  const tooltipW = 340;
  let tipTop, tipLeft;
  if (rect) {
    tipTop = rect.bottom + pad;
    tipLeft = rect.left;
    if (tipLeft + tooltipW > vw - 16) tipLeft = Math.max(16, vw - tooltipW - 16);
    // If placement is bottom-left explicitly, right-align the tip under the target
    if (step.placement === 'bottom-left') {
      tipLeft = Math.max(16, rect.right - tooltipW);
    }
    // If not enough space below, flip above
    if (tipTop + 180 > window.innerHeight - 16) {
      tipTop = Math.max(16, rect.top - 180 - pad);
    }
  } else {
    // No target found — center fallback
    tipTop = window.innerHeight / 2 - 90;
    tipLeft = vw / 2 - tooltipW / 2;
  }

  return (
    <div data-testid="walkthrough-tour" style={{ position: 'fixed', inset: 0, zIndex: 95, pointerEvents: 'none' }}>
      {/* Backdrop with a cutout for the highlighted element (SVG mask) */}
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }}
        onClick={() => {}} /* absorb clicks on backdrop, don't dismiss */
      >
        <defs>
          <mask id="luminex-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6} y={rect.top - 6}
                width={rect.width + 12} height={rect.height + 12}
                rx={10} ry={10} fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(10,14,26,0.55)" mask="url(#luminex-tour-mask)" />
        {/* Pulsing ring around target */}
        {rect && (
          <rect
            x={rect.left - 6} y={rect.top - 6}
            width={rect.width + 12} height={rect.height + 12}
            rx={10} ry={10}
            fill="none" stroke="#F26B1F" strokeWidth="2"
            style={{ filter: 'drop-shadow(0 0 8px rgba(242,107,31,0.7))' }}
          >
            <animate attributeName="stroke-opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
          </rect>
        )}
      </svg>

      {/* Tooltip */}
      <div
        data-testid="walkthrough-tooltip"
        style={{
          position: 'absolute', top: tipTop, left: tipLeft,
          width: tooltipW,
          background: 'var(--bg-1)',
          border: '1px solid var(--border-1)',
          borderRadius: 12,
          boxShadow: '0 18px 48px -12px rgba(10,14,26,0.45)',
          padding: '18px 20px',
          pointerEvents: 'auto',
          transition: 'top 200ms ease, left 200ms ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 2 }}>
            <Sparkle size={12} weight="fill" /> Step {idx + 1} of {steps.length}
          </div>
          <button onClick={() => finish(true)} data-testid="walkthrough-close-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 2 }}
            title="Skip tour">
            <X size={14} />
          </button>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em', margin: '0 0 6px' }}>
          {step.title}
        </h3>
        <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-2)', margin: '0 0 16px' }}>
          {step.body}
        </p>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
          {steps.map((_, i) => (
            <span key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i <= idx ? 'var(--accent)' : 'var(--bg-3)',
              transition: 'background 200ms ease',
            }} />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="btn btn-sm btn-ghost"
            data-testid="walkthrough-prev-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: idx === 0 ? 0.4 : 1, fontSize: 12 }}
          >
            <ArrowLeft size={12} /> Back
          </button>
          {isLast ? (
            <button onClick={() => finish(true)} className="btn btn-primary btn-sm" data-testid="walkthrough-finish-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              Finish <Sparkle size={12} weight="fill" />
            </button>
          ) : (
            <button onClick={() => setIdx(i => i + 1)} className="btn btn-primary btn-sm" data-testid="walkthrough-next-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              Next <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
